import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMomentJalaali } from '@mui/x-date-pickers/AdapterMomentJalaali';
import { faIR as faIRPickers } from '@mui/x-date-pickers/locales';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import * as XLSX from 'xlsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { authService } from '../services/authService';
import moment from 'moment-jalaali';
import { getDateStrForFilter, formatJalaliDateDisplay, parseIsoDateToMoment, momentToIsoDate } from '../utils/dateUtils';
import ReportFilterField from './ReportFilterField';
import ListPagination from './ListPagination';

const DETAIL_TABLE_PAGE_SIZE = 15;

const CLICKABLE_SX = {
  cursor: 'pointer',
  '&:hover': { color: '#0073ea', textDecoration: 'underline' },
};

const CHART_COLORS = [
  '#00a86a', '#0073ea', '#0073ea', '#00a86a', '#fdab3d',
  '#e44258', '#fdab3d', '#00a86a', '#fdab3d', '#0073ea',
  '#0073ea', '#00c875',
];

const CARD_SX = {
  borderRadius: 3,
  border: '1px solid rgba(0, 168, 106, 0.1)',
  boxShadow: '0 8px 32px rgba(50, 51, 56, 0.06)',
  bgcolor: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(8px)',
  overflow: 'hidden',
};

const LAYOUT = {
  kpi: {
    display: 'grid',
    width: '100%',
    gap: 2,
    mb: 3,
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      sm: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(6, minmax(0, 1fr))',
    },
  },
  top3: {
    display: 'grid',
    width: '100%',
    gap: 2,
    mb: 3,
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  },
  mainSplit: {
    display: 'grid',
    width: '100%',
    gap: 2,
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) minmax(0, 1fr)' },
  },
  twoCol: {
    display: 'grid',
    width: '100%',
    gap: 2,
    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
  },
};

const faIRDateLocaleText = {
  ...faIRPickers.components.MuiLocalizationProvider.defaultProps.localeText,
  fieldYearPlaceholder: () => 'سال',
  fieldMonthPlaceholder: () => 'ماه',
  fieldDayPlaceholder: () => 'روز',
};

const FILTER_INLINE_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  direction: 'rtl',
  width: '100%',
  minWidth: 0,
};

const FILTER_INLINE_LABEL_SX = {
  fontWeight: 700,
  color: '#676879',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontSize: '0.875rem',
};

const datePickerSlotProps = {
  textField: {
    size: 'small',
    fullWidth: true,
    placeholder: 'روز / ماه / سال',
    sx: {
      flex: 1,
      minWidth: 0,
      '& .MuiOutlinedInput-root': {
        direction: 'rtl',
        pr: 1.5,
        pl: 5,
        borderRadius: 2,
        bgcolor: '#fff',
      },
      '& .MuiInputBase-input': { cursor: 'pointer', textAlign: 'right' },
      '& .MuiInputAdornment-root': {
        position: 'absolute',
        left: 8,
        right: 'auto',
        margin: 0,
      },
    },
  },
};

const FILTER_SECTION_SX = {
  p: { xs: 1.5, sm: 2 },
  borderRadius: 2.5,
  bgcolor: '#f7f8fa',
  border: '1px solid #e6e9ef',
  direction: 'rtl',
  textAlign: 'right',
};

const FILTER_GRID_DATES = {
  display: 'grid',
  gap: 2,
  direction: 'rtl',
  justifyContent: 'start',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
};

const FILTER_GRID_SELECTS = {
  display: 'grid',
  gap: 2,
  direction: 'rtl',
  justifyContent: 'start',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(5, minmax(0, 1fr))',
  },
};

const fmt = (n) => (Number(n) || 0).toLocaleString('fa-IR');
const pct = (part, total) => (total > 0 ? ((part / total) * 100).toFixed(1) : '0');
const fmtAssay = (avg) => (avg != null ? `${Number(avg).toFixed(1)}%` : '—');

const aggregate = (entries, getKey, extraKeys = () => ({})) => {
  const map = {};
  entries.forEach((e) => {
    const key = getKey(e);
    if (!map[key]) {
      map[key] = { key, quantity: 0, count: 0, assaySum: 0, assayCount: 0, ...extraKeys(e) };
    }
    map[key].quantity += Number(e.quantity) || 0;
    map[key].count += 1;
    if (e.assayPercent != null && e.assayPercent !== '') {
      map[key].assaySum += Number(e.assayPercent);
      map[key].assayCount += 1;
    }
  });
  return Object.values(map)
    .map((row) => ({
      ...row,
      assayAvg: row.assayCount ? row.assaySum / row.assayCount : null,
    }))
    .sort((a, b) => b.quantity - a.quantity);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 2, boxShadow: 3, border: '1px solid #e6e9ef', minWidth: 160 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#323338', display: 'block', mb: 0.5 }}>
        {label || d.name || d.label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#00a86a', fontWeight: 700 }}>
        مقدار (کیلو): {fmt(payload[0]?.value)}
      </Typography>
      {d.count != null && (
        <Typography variant="caption" color="text.secondary">
          {fmt(d.count)} رکورد
        </Typography>
      )}
    </Box>
  );
};

const KpiCard = ({ icon, label, value, sub, color, gradient }) => (
  <Card sx={{ ...CARD_SX, position: 'relative' }}>
    <Box sx={{ height: 4, background: gradient || `linear-gradient(90deg, ${color}, ${color}88)` }} />
    <CardContent sx={{ py: 2, px: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#676879', fontWeight: 600, letterSpacing: 0.3 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#323338', mt: 0.5, lineHeight: 1.2 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" sx={{ color: '#c5c7d0', mt: 0.5, display: 'block' }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${color}18`, color,
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SectionCard = ({ title, subtitle, icon, children, action }) => (
  <Card sx={{ ...CARD_SX, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <CardContent sx={{ p: { xs: 2, md: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#323338' }}>{title}</Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
        </Box>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const CompanyReports = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const user = authService.getCurrentUser() || {};
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tablePages, setTablePages] = useState({});
  const [tablePageSizes, setTablePageSizes] = useState({});

  const activeTab = Number(searchParams.get('tab') || 0);
  const companyFilter = searchParams.get('company') || '';
  const productFilter = searchParams.get('product') || '';
  const documentTypeFilter = searchParams.get('documentType') || '';
  const locationFilter = searchParams.get('location') || '';
  const shiftFilter = searchParams.get('shift') || '';

  const parseDateParam = (key) => parseIsoDateToMoment(searchParams.get(key));
  const dateFrom = parseDateParam('dateFrom');
  const dateTo = parseDateParam('dateTo');

  const updateSearchParams = useCallback((updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, val]) => {
      if (val === '' || val == null || val === undefined) next.delete(key);
      else next.set(key, String(val));
    });
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  const setActiveTab = (tab) => updateSearchParams({ tab });

  const setDateFrom = (val) => {
    if (!val) {
      updateSearchParams({ dateFrom: '' });
      return;
    }
    const iso = momentToIsoDate(val);
    if (iso) updateSearchParams({ dateFrom: iso });
  };

  const setDateTo = (val) => {
    if (!val) {
      updateSearchParams({ dateTo: '' });
      return;
    }
    const iso = momentToIsoDate(val);
    if (iso) updateSearchParams({ dateTo: iso });
  };

  useEffect(() => {
    if (user.role !== 'admin') {
      navigate('/display');
      return;
    }
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        const res = await fetch(`${API_BASE_URL}/entries/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('خطا در دریافت اطلاعات');
        setEntries(await res.json());
      } catch (err) {
        setError(err.message || 'خطا در بارگذاری');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, user.role]);

  const companyList = useMemo(() => [...new Set(entries.map((e) => e.company || 'نامشخص'))].sort(), [entries]);
  const productList = useMemo(() => [...new Set(entries.map((e) => e.product || 'نامشخص'))].sort(), [entries]);
  const documentTypeList = useMemo(() => [...new Set(entries.map((e) => e.documentType || 'نامشخص'))].sort(), [entries]);
  const locationList = useMemo(() => [...new Set(entries.map((e) => e.location || 'نامشخص'))].sort(), [entries]);
  const shiftList = useMemo(() => [...new Set(entries.map((e) => e.shift || 'نامشخص'))].sort(), [entries]);

  const hasActiveFilter = Boolean(
    dateFrom || dateTo || companyFilter || productFilter || documentTypeFilter || locationFilter || shiftFilter
  );

  const isFilterActive = (field, value) => {
    if (!value) return false;
    const map = {
      company: companyFilter,
      product: productFilter,
      documentType: documentTypeFilter,
      location: locationFilter,
      shift: shiftFilter,
    };
    return map[field] === value;
  };

  const applyFilter = (field, value) => {
    const v = value ?? '';
    switch (field) {
      case 'company': updateSearchParams({ company: v }); break;
      case 'product': updateSearchParams({ product: v }); break;
      case 'documentType': updateSearchParams({ documentType: v }); break;
      case 'location': updateSearchParams({ location: v }); break;
      case 'shift': updateSearchParams({ shift: v }); break;
      case 'date': {
        if (!v) return;
        const iso = momentToIsoDate(moment(v, 'YYYY-MM-DD', true)) || momentToIsoDate(v);
        if (iso) {
          updateSearchParams({ dateFrom: iso, dateTo: iso });
        }
        break;
      }
      default: break;
    }
  };

  const ClickableFilter = ({ field, value, children, sx = {} }) => {
    const display = children ?? value;
    if (!field || value == null || value === '' || value === '-') return <>{display}</>;
    const active = isFilterActive(field, value);
    return (
      <Typography
        component="span"
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); applyFilter(field, value); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') applyFilter(field, value); }}
        sx={{
          ...CLICKABLE_SX,
          fontWeight: active ? 800 : 600,
          color: active ? '#0073ea' : 'inherit',
          ...sx,
        }}
      >
        {display}
      </Typography>
    );
  };

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (dateFrom || dateTo) {
      const fromStr = getDateStrForFilter(dateFrom);
      const toStr = getDateStrForFilter(dateTo);
      list = list.filter((e) => {
        const entryStr = getDateStrForFilter(e.date);
        if (!entryStr) return false;
        if (fromStr && entryStr < fromStr) return false;
        if (toStr && entryStr > toStr) return false;
        return true;
      });
    }
    if (companyFilter) list = list.filter((e) => (e.company || 'نامشخص') === companyFilter);
    if (productFilter) list = list.filter((e) => (e.product || 'نامشخص') === productFilter);
    if (documentTypeFilter) list = list.filter((e) => (e.documentType || 'نامشخص') === documentTypeFilter);
    if (locationFilter) list = list.filter((e) => (e.location || 'نامشخص') === locationFilter);
    if (shiftFilter) list = list.filter((e) => (e.shift || 'نامشخص') === shiftFilter);
    return list;
  }, [entries, dateFrom, dateTo, companyFilter, productFilter, documentTypeFilter, locationFilter, shiftFilter]);

  useEffect(() => {
    setTablePages({});
  }, [dateFrom, dateTo, companyFilter, productFilter, documentTypeFilter, locationFilter, shiftFilter]);

  const byCompany = useMemo(() =>
    aggregate(filteredEntries, (e) => e.company || 'نامشخص', (e) => ({ name: e.company || 'نامشخص' })),
    [filteredEntries]
  );

  const byProduct = useMemo(() =>
    aggregate(filteredEntries, (e) => e.product || 'نامشخص', (e) => ({ name: e.product || 'نامشخص' })),
    [filteredEntries]
  );

  const byDocumentType = useMemo(() =>
    aggregate(filteredEntries, (e) => e.documentType || 'نامشخص', (e) => ({ name: e.documentType || 'نامشخص' })),
    [filteredEntries]
  );

  const byLocation = useMemo(() =>
    aggregate(filteredEntries, (e) => e.location || 'نامشخص', (e) => ({ name: e.location || 'نامشخص' })),
    [filteredEntries]
  );

  const byShift = useMemo(() =>
    aggregate(filteredEntries, (e) => e.shift || 'نامشخص', (e) => ({ name: e.shift || 'نامشخص' })),
    [filteredEntries]
  );

  const byCompanyProduct = useMemo(() => {
    const map = {};
    filteredEntries.forEach((e) => {
      const company = e.company || 'نامشخص';
      const product = e.product || 'نامشخص';
      const key = `${company}|||${product}`;
      if (!map[key]) map[key] = { company, product, quantity: 0, count: 0, label: `${company} — ${product}` };
      map[key].quantity += Number(e.quantity) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.quantity - a.quantity);
  }, [filteredEntries]);

  const fullDetail = useMemo(() => {
    const map = {};
    filteredEntries.forEach((e) => {
      const key = `${e.company}|||${e.product}|||${e.documentType}`;
      if (!map[key]) {
        map[key] = {
          company: e.company || 'نامشخص',
          product: e.product || 'نامشخص',
          documentType: e.documentType || 'نامشخص',
          location: e.location || '-',
          quantity: 0,
          count: 0,
        };
      }
      map[key].quantity += Number(e.quantity) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.quantity - a.quantity);
  }, [filteredEntries]);

  const timeSeries = useMemo(() => {
    const map = {};
    filteredEntries.forEach((e) => {
      const d = getDateStrForFilter(e.date);
      if (!d) return;
      if (!map[d]) map[d] = { date: d, label: formatJalaliDateDisplay(e.date) || d, quantity: 0, count: 0 };
      map[d].quantity += Number(e.quantity) || 0;
      map[d].count += 1;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredEntries]);

  const companyDocMatrix = useMemo(() => {
    const companies = [...new Set(filteredEntries.map((e) => e.company || 'نامشخص'))].slice(0, 6);
    const docTypes = [...new Set(filteredEntries.map((e) => e.documentType || 'نامشخص'))];
    return companies.map((company) => {
      const row = { company };
      docTypes.forEach((dt) => {
        row[dt] = filteredEntries
          .filter((e) => (e.company || 'نامشخص') === company && (e.documentType || 'نامشخص') === dt)
          .reduce((s, e) => s + (Number(e.quantity) || 0), 0);
      });
      return row;
    });
  }, [filteredEntries]);

  const stats = useMemo(() => {
    const totalQty = filteredEntries.reduce((s, e) => s + (Number(e.quantity) || 0), 0);
    const edited = filteredEntries.filter((e) => e.isEdit).length;
    const withAssay = filteredEntries.filter((e) => e.assayPercent != null && e.assayPercent !== '');
    const assayAvg = withAssay.length
      ? withAssay.reduce((s, e) => s + Number(e.assayPercent), 0) / withAssay.length
      : null;
    const units = new Set(filteredEntries.map((e) => e.unit).filter(Boolean));
    return {
      count: filteredEntries.length,
      quantity: totalQty,
      companies: byCompany.length,
      products: byProduct.length,
      documentTypes: byDocumentType.length,
      locations: byLocation.length,
      avgPerRecord: filteredEntries.length ? Math.round(totalQty / filteredEntries.length) : 0,
      edited,
      assayAvg,
      units: units.size,
      topCompany: byCompany[0] || null,
      topProduct: byProduct[0] || null,
    };
  }, [filteredEntries, byCompany, byProduct, byDocumentType, byLocation]);

  const docTypesForStack = useMemo(() =>
    [...new Set(filteredEntries.map((e) => e.documentType || 'نامشخص'))].slice(0, 5),
    [filteredEntries]
  );

  const clearFilters = () => {
    updateSearchParams({
      company: '',
      product: '',
      documentType: '',
      location: '',
      shift: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const handleChartClick = (filterField, payload, nameKey = 'name') => {
    if (!filterField || !payload) return;
    applyFilter(filterField, payload[nameKey] ?? payload.name ?? payload.label);
  };

  const exportExcel = () => {
    const rows = fullDetail.map((r, i) => ({
      ردیف: i + 1,
      شرکت: r.company,
      محصول: r.product,
      'نوع سند': r.documentType,
      محل: r.location,
      'تعداد رکورد': r.count,
      'مقدار کل (کیلو)': r.quantity,
      'درصد از کل': `${pct(r.quantity, stats.quantity)}%`,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'گزارش تفصیلی');
    XLSX.writeFile(wb, `گزارش_ERP_${new Date().toLocaleDateString('fa-IR').replace(/\//g, '-')}.xlsx`);
  };

  const chartH = isSm ? 260 : isMd ? 300 : 340;

  const getBarChartHeight = (count, horizontal = true) =>
    horizontal ? Math.max(chartH, count * 44 + 48) : chartH;

  const getYAxisWidth = (data, nameKey) => {
    const maxLen = Math.max(...data.map((d) => String(d[nameKey] || '').length), 6);
    return Math.min(Math.max(maxLen * 8, isSm ? 110 : 140), 220);
  };

  const renderBarChart = (data, dataKey, nameKey, color = '#00a86a', horizontal = true, filterField = null) => {
    const sliced = data.slice(0, 12);
    const height = getBarChartHeight(sliced.length, horizontal);
    const axisW = horizontal ? getYAxisWidth(sliced, nameKey) : 0;
    return (
      <Box sx={{ width: '100%', minWidth: 0, height, direction: 'ltr' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={sliced}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{
              top: 8,
              right: 16,
              left: horizontal ? 4 : 4,
              bottom: horizontal ? 8 : 48,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f6f8" />
            {horizontal ? (
              <>
                <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 10, fill: '#676879' }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey={nameKey}
                  width={axisW}
                  tick={{ fontSize: 11, fill: '#323338' }}
                  tickFormatter={(v) => (v?.length > 20 ? `${v.slice(0, 18)}…` : v)}
                  axisLine={false}
                  tickLine={false}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey={nameKey}
                  tick={{ fontSize: 10, fill: '#323338' }}
                  tickFormatter={(v) => (v?.length > 12 ? `${v.slice(0, 10)}…` : v)}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={50}
                />
                <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#676879' }} axisLine={false} tickLine={false} width={70} />
              </>
            )}
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey={dataKey}
              name="مقدار (کیلو)"
              fill={color}
              radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
              maxBarSize={36}
              cursor={filterField ? 'pointer' : 'default'}
              onClick={(barData) => handleChartClick(filterField, barData, nameKey)}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderPieChart = (data, maxItems = 8, donut = false, filterField = null) => {
    const items = data.slice(0, maxItems).map((row, i) => ({
      name: row.name || row.label,
      value: row.quantity ?? row.value,
      count: row.count,
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }));
    const total = items.reduce((s, r) => s + r.value, 0);
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <Box sx={{ width: '100%', height: isSm ? 240 : 280, direction: 'ltr' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={items}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={donut ? (isSm ? 50 : 65) : 0}
                outerRadius={isSm ? 85 : 105}
                paddingAngle={2}
                cursor={filterField ? 'pointer' : 'default'}
                onClick={(slice) => filterField && applyFilter(filterField, slice?.name)}
              >
                {items.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} stroke="white" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 1,
          mt: 2,
        }}>
          {items.map((item) => (
            <Box
              key={item.name}
              onClick={() => filterField && applyFilter(filterField, item.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: isFilterActive(filterField, item.name) ? '#e5f4ff' : '#f7f8fa',
                border: `1px solid ${isFilterActive(filterField, item.name) ? '#00a86a' : '#e6e9ef'}`,
                minWidth: 0,
                cursor: filterField ? 'pointer' : 'default',
                '&:hover': filterField ? { borderColor: '#00a86a', bgcolor: '#e5f4ff' } : {},
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.fill, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, color: '#323338', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.name}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#00a86a', flexShrink: 0 }}>
                {pct(item.value, total)}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const TimeSeriesDot = ({ cx, cy, payload, fill = '#00a86a' }) => {
    if (cx == null || cy == null) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={fill}
        stroke="#fff"
        strokeWidth={1.5}
        style={{ cursor: 'pointer' }}
        onClick={() => payload?.date && applyFilter('date', payload.date)}
      />
    );
  };

  const renderDetailTable = (tableKey, rows, columns) => {
    const pageSize = tablePageSizes[tableKey] || DETAIL_TABLE_PAGE_SIZE;
    const total = rows?.length || 0;
    const pageCount = total === 0 ? 0 : Math.ceil(total / pageSize);
    const rawPage = tablePages[tableKey] || 0;
    const page = Math.min(rawPage, Math.max(0, pageCount - 1));
    const start = page * pageSize;
    const pageRows = (rows || []).slice(start, start + pageSize);

    const setPage = (next) => {
      setTablePages((prev) => ({ ...prev, [tableKey]: typeof next === 'function' ? next(page) : next }));
    };
    const setPageSize = (size) => {
      setTablePageSizes((prev) => ({ ...prev, [tableKey]: size }));
      setTablePages((prev) => ({ ...prev, [tableKey]: 0 }));
    };

    return (
      <Box>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e6e9ef', maxHeight: 480 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f7f8fa' }}>
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align || 'right'} sx={{ fontWeight: 700, color: '#323338', whiteSpace: 'nowrap' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row, i) => {
                const absoluteIndex = start + i;
                return (
                  <TableRow
                    key={absoluteIndex}
                    sx={{
                      '&:hover': { bgcolor: '#f7f8fa' },
                      '&:nth-of-type(even)': { bgcolor: '#f7f8fa' },
                    }}
                  >
                    {columns.map((col) => {
                      const filterValue = col.filterValue ? col.filterValue(row) : row[col.id];
                      const cellContent = col.render ? col.render(row, absoluteIndex) : row[col.id];
                      return (
                        <TableCell key={col.id} align={col.align || 'right'}>
                          {col.filterField ? (
                            <ClickableFilter field={col.filterField} value={filterValue}>
                              {cellContent}
                            </ClickableFilter>
                          ) : cellContent}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 1.5 }}>
          <ListPagination
            page={page}
            pageCount={pageCount}
            totalCount={total}
            pageSize={pageSize}
            showPageSize
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </Box>
      </Box>
    );
  };

  if (user.role !== 'admin') return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#00a86a' }} />
        <Typography color="text.secondary">در حال بارگذاری گزارش...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ py: 6, px: 2, maxWidth: 560, mx: 'auto' }}>
        <Card sx={{ ...CARD_SX, textAlign: 'center', p: 4 }}>
          <AssessmentIcon sx={{ fontSize: 48, color: '#00a86a', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>گزارش تحلیلی</Typography>
          <Typography color="text.secondary">هنوز داده‌ای ثبت نشده است.</Typography>
        </Card>
      </Box>
    );
  }

  const noDataAfterFilter = hasActiveFilter && filteredEntries.length === 0;

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      background: 'linear-gradient(180deg, #f7f8fa 0%, #e5f4ff 50%, #f7f8fa 100%)',
      pb: 4,
      px: { xs: 1.5, sm: 2, md: 3 },
      boxSizing: 'border-box',
    }}>
      {/* Hero Header */}
      <Box sx={{
        mt: 1, mb: 3, p: { xs: 2.5, md: 3.5 },
        borderRadius: 4,
        background: '#ffffff',
        color: 'white',
        boxShadow: '0 20px 60px rgba(0, 168, 106, 0.35)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -40, left: -40, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ position: 'absolute', bottom: -60, right: -20, width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>
                داشبورد گزارش‌گیری
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                تحلیل جامع داده‌های ثبت‌شده — شرکت، محصول، سند و روند زمانی
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={exportExcel}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                fontWeight: 700,
                borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              خروجی Excel
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
            <Chip label={`${fmt(stats.count)} رکورد`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
            <Chip label={`${fmt(stats.companies)} شرکت`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
            <Chip label={`${fmt(stats.products)} محصول`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
            <Chip label={`مقدار کل (کیلو): ${fmt(stats.quantity)}`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ ...CARD_SX, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#323338' }}>فیلترهای گزارش</Typography>
            {hasActiveFilter && (
              <Button size="small" startIcon={<FilterAltOffIcon />} onClick={clearFilters} sx={{ borderRadius: 2 }}>
                پاک کردن فیلترها
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={FILTER_SECTION_SX}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 700, color: '#676879' }}>
                بازه زمانی
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMomentJalaali} localeText={faIRDateLocaleText}>
                <Box sx={FILTER_GRID_DATES}>
                  <Box sx={FILTER_INLINE_ROW_SX}>
                    <Typography component="span" sx={FILTER_INLINE_LABEL_SX}>
                      از تاریخ
                    </Typography>
                    <DesktopDatePicker
                      format="jYYYY/jMM/jDD"
                      value={dateFrom}
                      onChange={setDateFrom}
                      maxDate={dateTo || undefined}
                      slotProps={datePickerSlotProps}
                    />
                  </Box>
                  <Box sx={FILTER_INLINE_ROW_SX}>
                    <Typography component="span" sx={FILTER_INLINE_LABEL_SX}>
                      تا تاریخ
                    </Typography>
                    <DesktopDatePicker
                      format="jYYYY/jMM/jDD"
                      value={dateTo}
                      onChange={setDateTo}
                      minDate={dateFrom || undefined}
                      slotProps={datePickerSlotProps}
                    />
                  </Box>
                </Box>
              </LocalizationProvider>
            </Box>

            <Box sx={FILTER_SECTION_SX}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 700, color: '#676879' }}>
                فیلتر داده‌ها
              </Typography>
              <Box sx={FILTER_GRID_SELECTS}>
                {[
                  { label: 'شرکت', key: 'company', value: companyFilter, list: companyList },
                  { label: 'محصول', key: 'product', value: productFilter, list: productList },
                  { label: 'نوع سند', key: 'documentType', value: documentTypeFilter, list: documentTypeList },
                  { label: 'محل', key: 'location', value: locationFilter, list: locationList },
                  { label: 'شیفت', key: 'shift', value: shiftFilter, list: shiftList },
                ].map((f) => (
                  <ReportFilterField
                    key={f.label}
                    label={f.label}
                    value={f.value}
                    onChange={(val) => updateSearchParams({ [f.key]: val })}
                    options={f.list}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {noDataAfterFilter && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>با این فیلتر داده‌ای یافت نشد.</Alert>
      )}

      {/* KPI */}
      <Box sx={LAYOUT.kpi}>
        {[
          { icon: <AssessmentIcon />, label: 'کل رکوردها', value: fmt(stats.count), sub: `${fmt(stats.edited)} ویرایش‌شده`, color: '#00a86a' },
          { icon: <BusinessIcon />, label: 'شرکت فعال', value: fmt(stats.companies), sub: stats.topCompany ? (
            <>بیشترین: <ClickableFilter field="company" value={stats.topCompany.name}>{stats.topCompany.name}</ClickableFilter></>
          ) : '', color: '#0073ea' },
          { icon: <InventoryIcon />, label: 'محصولات', value: fmt(stats.products), sub: stats.topProduct ? (
            <>پرتکرار: <ClickableFilter field="product" value={stats.topProduct.name}>{stats.topProduct.name}</ClickableFilter></>
          ) : '', color: '#0073ea' },
          { icon: <TrendingUpIcon />, label: 'مقدار کل (کیلو)', value: fmt(stats.quantity), sub: `${fmt(stats.count)} رکورد ثبت‌شده`, color: '#00a86a' },
          { icon: <DescriptionIcon />, label: 'انواع سند', value: fmt(stats.documentTypes), sub: `${fmt(stats.locations)} محل ثبت`, color: '#fdab3d' },
          { icon: <TimelineIcon />, label: 'روند داده', value: fmt(timeSeries.length), sub: stats.assayAvg != null ? `میانگین عیار: ${stats.assayAvg.toFixed(1)}%` : `${fmt(stats.units)} واحد`, color: '#fdab3d' },
        ].map((kpi, i) => (
          <Box key={i} sx={{ minWidth: 0 }}>
            <KpiCard {...kpi} />
          </Box>
        ))}
      </Box>

      {/* Top 3 Rankings */}
      <Box sx={LAYOUT.top3}>
        {byCompany.slice(0, 3).map((row, i) => (
          <Box key={row.name} sx={{ minWidth: 0 }}>
            <Card
              onClick={() => applyFilter('company', row.name)}
              sx={{
              ...CARD_SX,
              background: `linear-gradient(135deg, ${CHART_COLORS[i]}12 0%, white 100%)`,
              borderColor: isFilterActive('company', row.name) ? CHART_COLORS[i] : `${CHART_COLORS[i]}33`,
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 40px rgba(50, 51, 56, 0.1)' },
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    bgcolor: CHART_COLORS[i], color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 900, fontSize: '1rem',
                  }}>
                    {i + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: '#323338', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{fmt(row.count)} رکورد</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: CHART_COLORS[i] }}>{fmt(row.quantity)}</Typography>
                    <Typography variant="caption" sx={{ color: '#676879' }}>{pct(row.quantity, stats.quantity)}%</Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.quantity ? (row.quantity / stats.quantity) * 100 : 0}
                  sx={{ mt: 1.5, height: 6, borderRadius: 3, bgcolor: '#e6e9ef', '& .MuiLinearProgress-bar': { bgcolor: CHART_COLORS[i], borderRadius: 3 } }}
                />
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Tabs */}
      <Card sx={{ ...CARD_SX, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e6e9ef',
            '& .MuiTab-root': { fontWeight: 700, minHeight: 52, color: '#676879' },
            '& .Mui-selected': { color: '#0073ea' },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: '#0073ea' },
          }}
        >
          <Tab icon={<AssessmentIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="نمای کلی" />
          <Tab icon={<BusinessIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="شرکت‌ها" />
          <Tab icon={<InventoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="محصولات" />
          <Tab icon={<DescriptionIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="اسناد" />
          <Tab icon={<TimelineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="روند زمانی" />
          <Tab icon={<TableChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="جدول تفصیلی" />
        </Tabs>
      </Card>

      {/* Tab: Overview */}
      {activeTab === 0 && (
        <Box sx={LAYOUT.stack}>
          <Box sx={LAYOUT.mainSplit}>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="مقدار (کیلو) به تفکیک شرکت" subtitle="۱۲ شرکت برتر — کلیک برای فیلتر" icon={<BarChartIcon sx={{ color: '#00a86a' }} />}>
                {renderBarChart(byCompany, 'quantity', 'name', '#00a86a', true, 'company')}
              </SectionCard>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="سهم شرکت‌ها" subtitle="توزیع درصدی — کلیک برای فیلتر" icon={<AssessmentIcon sx={{ color: '#0073ea' }} />}>
                {renderPieChart(byCompany, 10, true, 'company')}
              </SectionCard>
            </Box>
          </Box>
          <Box sx={LAYOUT.twoCol}>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="توزیع نوع سند" subtitle="کلیک برای فیلتر" icon={<DescriptionIcon sx={{ color: '#fdab3d' }} />}>
                {renderBarChart(byDocumentType, 'quantity', 'name', '#fdab3d', false, 'documentType')}
              </SectionCard>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="محصولات پرتکرار" subtitle="کلیک برای فیلتر" icon={<InventoryIcon sx={{ color: '#0073ea' }} />}>
                {renderBarChart(byProduct, 'quantity', 'name', '#0073ea', true, 'product')}
              </SectionCard>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="توزیع محل" subtitle="کلیک برای فیلتر" icon={<BusinessIcon sx={{ color: '#00a86a' }} />}>
                {renderBarChart(byLocation, 'quantity', 'name', '#00a86a', false, 'location')}
              </SectionCard>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="توزیع شیفت" subtitle="کلیک برای فیلتر" icon={<TimelineIcon sx={{ color: '#fdab3d' }} />}>
                {renderBarChart(byShift, 'quantity', 'name', '#fdab3d', false, 'shift')}
              </SectionCard>
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab: Companies */}
      {activeTab === 1 && (
        <Box sx={LAYOUT.stack}>
          <SectionCard title="جدول تحلیلی شرکت‌ها" subtitle="شامل سهم و مقدار" icon={<BusinessIcon sx={{ color: '#00a86a' }} />}>
            {renderDetailTable('byCompany', byCompany, [
              { id: 'rank', label: '#', render: (_, i) => i + 1 },
              { id: 'name', label: 'شرکت', filterField: 'company' },
              { id: 'count', label: 'رکورد', align: 'center', render: (r) => fmt(r.count) },
              { id: 'quantity', label: 'مقدار کل (کیلو)', align: 'center', render: (r) => <Typography sx={{ fontWeight: 800, color: '#00a86a' }}>{fmt(r.quantity)}</Typography> },
              { id: 'pct', label: 'سهم', align: 'center', render: (r) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
                  <LinearProgress variant="determinate" value={stats.quantity ? (r.quantity / stats.quantity) * 100 : 0} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{pct(r.quantity, stats.quantity)}%</Typography>
                </Box>
              ) },
            ])}
          </SectionCard>
          {docTypesForStack.length > 0 && (
            <SectionCard title="ماتریس شرکت × نوع سند" subtitle="۶ شرکت برتر" icon={<TableChartIcon sx={{ color: '#0073ea' }} />}>
              <Box sx={{ width: '100%', height: chartH + 40, direction: 'ltr' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={companyDocMatrix} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f6f8" />
                    <XAxis dataKey="company" tick={{ fontSize: 10 }} tickFormatter={(v) => (v?.length > 10 ? `${v.slice(0, 8)}…` : v)} />
                    <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {docTypesForStack.map((dt, i) => (
                      <Bar
                        key={dt}
                        dataKey={dt}
                        stackId="a"
                        fill={CHART_COLORS[i % CHART_COLORS.length]}
                        radius={i === docTypesForStack.length - 1 ? [4, 4, 0, 0] : 0}
                        cursor="pointer"
                        onClick={(barData) => {
                          const updates = { documentType: dt };
                          if (barData?.company) updates.company = barData.company;
                          updateSearchParams(updates);
                        }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </SectionCard>
          )}
          <SectionCard title="شرکت × محصول" subtitle="ترکیب دقیق‌تر" icon={<InventoryIcon sx={{ color: '#0073ea' }} />}>
            {renderDetailTable('byCompanyProduct', byCompanyProduct, [
              { id: 'rank', label: '#', render: (_, i) => i + 1 },
              { id: 'company', label: 'شرکت', filterField: 'company' },
              { id: 'product', label: 'محصول', filterField: 'product' },
              { id: 'count', label: 'رکورد', align: 'center', render: (r) => fmt(r.count) },
              { id: 'quantity', label: 'مقدار (کیلو)', align: 'center', render: (r) => fmt(r.quantity) },
              { id: 'pct', label: '%', align: 'center', render: (r) => `${pct(r.quantity, stats.quantity)}%` },
            ])}
          </SectionCard>
        </Box>
      )}

      {/* Tab: Products */}
      {activeTab === 2 && (
        <Box sx={LAYOUT.stack}>
          <SectionCard title="جدول کامل محصولات" icon={<TableChartIcon />}>
            {renderDetailTable('byProduct', byProduct, [
              { id: 'rank', label: '#', render: (_, i) => i + 1 },
              { id: 'name', label: 'محصول', filterField: 'product' },
              { id: 'count', label: 'رکورد', align: 'center', render: (r) => fmt(r.count) },
              { id: 'quantity', label: 'مقدار کل (کیلو)', align: 'center', render: (r) => fmt(r.quantity) },
              { id: 'assay', label: 'عیار', align: 'center', render: (r) => fmtAssay(r.assayAvg) },
            ])}
          </SectionCard>
          <Box sx={LAYOUT.twoCol}>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="نمودار محصولات" icon={<InventoryIcon sx={{ color: '#0073ea' }} />}>
                {renderBarChart(byProduct, 'quantity', 'name', '#0073ea', true, 'product')}
              </SectionCard>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="سهم محصولات" icon={<AssessmentIcon sx={{ color: '#0073ea' }} />}>
                {renderPieChart(byProduct, 10, false, 'product')}
              </SectionCard>
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab: Documents */}
      {activeTab === 3 && (
        <Box sx={LAYOUT.stack}>
          <Box sx={LAYOUT.mainSplit}>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="تحلیل نوع سند" icon={<DescriptionIcon sx={{ color: '#fdab3d' }} />}>
                {renderBarChart(byDocumentType, 'quantity', 'name', '#fdab3d', false, 'documentType')}
              </SectionCard>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionCard title="سهم انواع سند" icon={<AssessmentIcon />}>
                {renderPieChart(byDocumentType, 8, true, 'documentType')}
              </SectionCard>
            </Box>
          </Box>
          <SectionCard title="جدول انواع سند" icon={<TableChartIcon />}>
            {renderDetailTable('byDocumentType', byDocumentType, [
              { id: 'rank', label: '#', render: (_, i) => i + 1 },
              { id: 'name', label: 'نوع سند', filterField: 'documentType' },
              { id: 'count', label: 'تعداد ثبت', align: 'center', render: (r) => fmt(r.count) },
              { id: 'quantity', label: 'مقدار (کیلو)', align: 'center', render: (r) => fmt(r.quantity) },
              { id: 'pct', label: 'سهم از کل', align: 'center', render: (r) => `${pct(r.quantity, stats.quantity)}%` },
            ])}
          </SectionCard>
        </Box>
      )}

      {/* Tab: Time Trend */}
      {activeTab === 4 && (
        <Box sx={LAYOUT.stack}>
          <SectionCard title="روند مقدار (کیلو) در زمان" subtitle="بر اساس تاریخ ثبت" icon={<TimelineIcon sx={{ color: '#00a86a' }} />}>
            <Box sx={{ width: '100%', height: chartH + 60, direction: 'ltr' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeSeries} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <defs>
                    <linearGradient id="qtyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00a86a" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#00a86a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f6f8" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#676879' }} interval={isSm ? 'preserveStartEnd' : Math.floor(timeSeries.length / 8)} angle={-35} textAnchor="end" height={50} />
                  <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="quantity" name="مقدار (کیلو)" stroke="#00a86a" strokeWidth={2.5} fill="url(#qtyGrad)" dot={<TimeSeriesDot fill="#00a86a" />} activeDot={{ r: 6, onClick: (_, payload) => payload?.payload?.date && applyFilter('date', payload.payload.date) }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
          <SectionCard title="روند تعداد رکوردها" subtitle="تعداد ثبت در هر روز" icon={<TrendingUpIcon sx={{ color: '#00a86a' }} />}>
            <Box sx={{ width: '100%', height: chartH, direction: 'ltr' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeries} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f6f8" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={Math.floor(timeSeries.length / 8)} angle={-35} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="count" name="رکورد" stroke="#00a86a" strokeWidth={2.5} dot={<TimeSeriesDot fill="#00a86a" />} activeDot={{ r: 6, onClick: (_, payload) => payload?.payload?.date && applyFilter('date', payload.payload.date) }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
          <SectionCard title="جدول روزانه" icon={<TableChartIcon />}>
            {renderDetailTable('timeSeries', timeSeries, [
              { id: 'label', label: 'تاریخ', filterField: 'date', filterValue: (r) => r.date },
              { id: 'count', label: 'رکورد', align: 'center', render: (r) => fmt(r.count) },
              { id: 'quantity', label: 'مقدار (کیلو)', align: 'center', render: (r) => fmt(r.quantity) },
            ])}
          </SectionCard>
        </Box>
      )}

      {/* Tab: Full Detail Table */}
      {activeTab === 5 && (
        <SectionCard
          title="جدول تفصیلی کامل"
          subtitle="ترکیب شرکت + محصول + نوع سند + محل"
          icon={<TableChartIcon sx={{ color: '#0073ea' }} />}
          action={
            <Button size="small" startIcon={<FileDownloadIcon />} onClick={exportExcel} variant="outlined" sx={{ borderRadius: 2 }}>
              Excel
            </Button>
          }
        >
          {renderDetailTable('fullDetail', fullDetail, [
            { id: 'rank', label: '#', render: (_, i) => i + 1 },
            { id: 'company', label: 'شرکت', filterField: 'company' },
            { id: 'product', label: 'محصول', filterField: 'product' },
            { id: 'documentType', label: 'نوع سند', filterField: 'documentType' },
            { id: 'location', label: 'محل', filterField: 'location' },
            { id: 'count', label: 'رکورد', align: 'center', render: (r) => fmt(r.count) },
            { id: 'quantity', label: 'مقدار (کیلو)', align: 'center', render: (r) => <Typography sx={{ fontWeight: 700, color: '#0073ea' }}>{fmt(r.quantity)}</Typography> },
            { id: 'pct', label: '% کل', align: 'center', render: (r) => `${pct(r.quantity, stats.quantity)}%` },
          ])}
        </SectionCard>
      )}
    </Box>
  );
};

export default CompanyReports;
